<template>
  <div class="gym-receipt-print">
    <!-- Header -->
    <div class="receipt-header">
      <h1 class="business-name">{{ businessName || 'Your Gym' }}</h1>
      <div class="business-contact">
        <p v-if="businessAddress">{{ businessAddress }}</p>
        <p v-if="businessPhone || businessEmail">
          <span v-if="businessPhone">Ph: {{ businessPhone }}</span>
          <span v-if="businessPhone && businessEmail"> | </span>
          <span v-if="businessEmail">Email: {{ businessEmail }}</span>
        </p>
      </div>
      <div class="receipt-title">PAYMENT RECEIPT</div>
    </div>

    <!-- Receipt Info -->
    <div class="receipt-info">
      <div class="info-row">
        <span><strong>Receipt No:</strong> {{ payment?.receipt_number || 'N/A' }}</span>
        <span><strong>Date:</strong> {{ formatDate(payment?.payment_date) }}</span>
      </div>
    </div>

    <!-- Member Details Box -->
    <div class="member-details">
      <h4>MEMBER DETAILS</h4>
      <div class="details-grid">
        <div class="detail-row">
          <span class="label">Name:</span>
          <span class="value">{{ member?.name || payment?.member_name }}</span>
        </div>
        <div class="detail-row">
          <span class="label">Member ID:</span>
          <span class="value">{{ member?.member_number || payment?.member_number || '-' }}</span>
        </div>
        <div class="detail-row">
          <span class="label">Phone:</span>
          <span class="value">{{ member?.phone || payment?.member_phone || '-' }}</span>
        </div>
      </div>
    </div>

    <!-- Payment Details Table -->
    <div class="payment-section">
      <h4>PAYMENT DETAILS</h4>
      <table class="payment-table">
        <thead>
          <tr>
            <th>Plan</th>
            <th>Validity Period</th>
            <th>Mode</th>
            <th class="text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{{ payment?.plan_name || 'Membership' }}</td>
            <td class="text-center">{{ formatDate(payment?.membership_start) }} - {{ formatDate(payment?.membership_end) }}</td>
            <td class="text-center">{{ capitalizeFirst(payment?.payment_mode) }}</td>
            <td class="text-right amount-cell">{{ formatCurrency(payment?.amount) }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Amount in Words -->
    <div class="amount-words">
      <strong>Amount in Words:</strong> {{ amountInWords }}
    </div>

    <!-- Total Box -->
    <div class="total-box">
      <span>Total: {{ formatCurrency(payment?.amount) }}</span>
    </div>

    <!-- Signature Section -->
    <div class="signatures">
      <div class="received-text">Received with thanks</div>
      <div class="signature-box">
        <div class="signature-line"></div>
        <p>Authorized Signature</p>
      </div>
    </div>

    <!-- Footer -->
    <div v-if="footerText" class="receipt-footer">
      <p>{{ footerText }}</p>
    </div>

    <!-- Powered By -->
    <div class="powered-by">
      Generated by OnePlaceHQ
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import dayjs from 'dayjs'
import { amountInWords as toWords } from '@shared/pdf/utils.js'

const props = defineProps({
  payment: {
    type: Object,
    required: true,
  },
  member: {
    type: Object,
    default: () => ({}),
  },
  businessName: {
    type: String,
    default: '',
  },
  businessAddress: {
    type: String,
    default: '',
  },
  businessPhone: {
    type: String,
    default: '',
  },
  businessEmail: {
    type: String,
    default: '',
  },
  footerText: {
    type: String,
    default: '',
  },
})

// Format date
const formatDate = (date) => {
  if (!date) return ''
  return dayjs(date).format('DD MMM YYYY')
}

// Format currency (INR with Indian numbering)
const formatCurrency = (value) => {
  if (!value && value !== 0) return 'Rs. 0'
  return 'Rs. ' + new Intl.NumberFormat('en-IN').format(value)
}

// Capitalize first letter
const capitalizeFirst = (str) => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

const amountInWords = computed(() => {
  return toWords(props.payment?.amount || 0)
})
</script>

<style scoped>
/* Print-specific styles */
.gym-receipt-print {
  width: 210mm;
  min-height: 297mm;
  padding: 15mm;
  margin: 0 auto;
  background: white;
  font-family: 'Arial', sans-serif;
  font-size: 11pt;
  color: #000;
  box-sizing: border-box;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* Header */
.receipt-header {
  text-align: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ccc;
}

.business-name {
  font-size: 18pt;
  font-weight: bold;
  margin: 0 0 5px 0;
  color: #1f2937;
}

.business-contact {
  font-size: 9pt;
  color: #666;
  margin-bottom: 8px;
}

.business-contact p {
  margin: 2px 0;
}

.receipt-title {
  font-size: 14pt;
  font-weight: bold;
  color: #3b82f6;
  margin-top: 10px;
}

/* Receipt Info */
.receipt-info {
  margin-bottom: 15px;
  padding: 8px 15px;
  background: #f5f5f5;
}

.info-row {
  display: flex;
  justify-content: space-between;
}

/* Member Details */
.member-details {
  padding: 12px 15px;
  margin-bottom: 15px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}

.member-details h4 {
  font-size: 11pt;
  font-weight: bold;
  margin: 0 0 10px 0;
  color: #1f2937;
}

.details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px 20px;
}

.detail-row {
  display: flex;
  gap: 10px;
}

.detail-row .label {
  font-weight: bold;
  font-size: 9pt;
}

.detail-row .value {
  font-size: 10pt;
}

/* Payment Section */
.payment-section h4 {
  font-size: 11pt;
  font-weight: bold;
  margin: 0 0 10px 0;
  color: #1f2937;
}

.payment-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 15px;
}

.payment-table th {
  background: #3b82f6;
  color: white;
  padding: 8px 12px;
  font-size: 10pt;
  border: 1px solid #3b82f6;
  text-align: left;
}

.payment-table td {
  border: 1px solid #ddd;
  padding: 10px 12px;
  font-size: 10pt;
}

.text-center {
  text-align: center;
}

.text-right {
  text-align: right;
}

.amount-cell {
  font-weight: bold;
}

/* Amount in Words */
.amount-words {
  padding: 10px 15px;
  margin-bottom: 15px;
  background: #f9fafb;
  font-size: 10pt;
}

/* Total Box */
.total-box {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 30px;
}

.total-box span {
  background: #3b82f6;
  color: white;
  padding: 8px 25px;
  font-weight: bold;
  font-size: 12pt;
}

/* Signatures */
.signatures {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 40px;
}

.received-text {
  font-size: 10pt;
  color: #666;
}

.signature-box {
  text-align: center;
  min-width: 180px;
}

.signature-line {
  border-bottom: 1px solid #000;
  margin-bottom: 5px;
  height: 50px;
}

.signature-box p {
  margin: 0;
  font-size: 9pt;
  color: #666;
}

/* Footer */
.receipt-footer {
  margin-top: 20px;
  padding-top: 10px;
  border-top: 1px dashed #ccc;
  text-align: center;
  font-size: 9pt;
  color: #666;
  font-style: italic;
}

.receipt-footer p {
  margin: 0;
}

/* Powered By */
.powered-by {
  margin-top: 20px;
  text-align: center;
  font-size: 7pt;
  color: #bbb;
}

/* Print styles */
@media print {
  .gym-receipt-print {
    width: 100%;
    margin: 0;
    padding: 10mm;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* Force backgrounds on all child elements */
  .gym-receipt-print * {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  @page {
    size: A4;
    margin: 0;
  }
}
</style>
